import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { followRedirect } from "@/lib/site";
import { toast } from "sonner";
import api from "@/lib/api";
import { initMetaPixel, trackLead, trackEvent } from "@/lib/metaPixel";
import { injectSchema, setMeta, setCanonical } from "@/lib/seo";
import {
  BedDouble, Bath, Ruler, Calendar, MapPin, Phone, Mail, Globe,
  X, ChevronLeft, ChevronRight, Home as HomeIcon, FileText, Download,
} from "lucide-react";

const fmtPrice = (n) => n ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n) : "Price upon request";
const STAGE = { coming_soon: "Coming Soon", for_sale: "For Sale", under_contract: "Under Contract", sold: "Sold" };
const toEmbed = (url) => {
  if (!url) return "";
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const v = url.match(/vimeo\.com\/(\d+)/);
  if (v) return `https://player.vimeo.com/video/${v[1]}`;
  const mu = url.match(/muse\.ai\/(?:v|embed)\/([\w-]+)/);
  if (mu) return `https://muse.ai/embed/${mu[1]}`;
  return url;
};

export default function PublicListing() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [prop, setProp] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    initMetaPixel();
    api.get(`/lm/public/property/${slug}`).then(({ data }) => {
      setProp(data);
      const city = data.city ? ", " + data.city : "";
      document.title = `${data.address_line}${city}`;
      setMeta("description", (data.description || `${data.address_line}${city} — view photos, details and schedule a tour.`).slice(0, 155));
      setMeta("robots", "index, follow");
      setMeta("og:type", "website", true); setMeta("og:title", `${data.address_line}${city}`, true);
      setMeta("og:image", data.structured_data?.[0]?.image || "", true);
      setMeta("og:url", data.canonical_url, true);
      setMeta("twitter:card", "summary_large_image");
      setCanonical(data.canonical_url);
      injectSchema(data.structured_data);
    }).catch(async (err) => {
      if (!(await followRedirect(`/p/${slug}`, navigate, err.response?.data?.redirect))) {
        setMeta("robots", "noindex");
        setNotFound(true);
      }
    });
    let sid = localStorage.getItem("mh_sid");
    if (!sid) { sid = `${Date.now()}-${Math.random().toString(36).slice(2)}`; localStorage.setItem("mh_sid", sid); }
    const params = new URLSearchParams(window.location.search);
    api.post("/lm/public/track", { slug, referrer: document.referrer || "", session_id: sid, utm_source: params.get("utm_source") || "" }).catch(() => {});
  }, [slug, navigate]);

  if (notFound) return (
    <div className="min-h-screen grid place-items-center bg-[#FAFAF9]" data-testid="listing-gone">
      <div className="text-center"><HomeIcon className="w-10 h-10 mx-auto text-stone-300" /><h1 className="mt-3 text-2xl font-serif text-stone-800">This listing is no longer available</h1><p className="text-stone-500 mt-1">It may have sold or been removed.</p><a href="/browse" data-testid="listing-gone-browse" className="inline-block mt-5 bg-stone-900 text-white text-sm px-5 py-2.5 rounded-full hover:bg-stone-700 transition-colors">Browse current listings</a></div>
    </div>
  );
  if (!prop) return <div className="min-h-screen grid place-items-center bg-[#FAFAF9] text-stone-400">Loading…</div>;

  const photos = prop.photos?.length ? prop.photos : (prop.hero_photo ? [prop.hero_photo] : []);
  const hero = photos[0] || prop.hero_photo || "";
  const walk = prop.video_walkthrough ? (prop.video_walkthrough.startsWith("http") ? prop.video_walkthrough : `${process.env.REACT_APP_BACKEND_URL}${prop.video_walkthrough}`) : "";
  const agent = prop.agent;
  const cityLine = [prop.city, `${prop.state || ""}${prop.zip_code ? " " + prop.zip_code : ""}`].filter(Boolean).join(", ");
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent([prop.address_line, cityLine].filter(Boolean).join(", "))}&output=embed`;
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const links = [["details", "Details"], photos.length && ["photos", "Photos"], prop.video_walkthrough && ["walkthrough", "Property Tour"], prop.video_url && ["video", "Video"], prop.tour_3d_url && ["tour3d", "3D Tour"], prop.floor_plans?.length && ["floorplans", "Floor Plans"], prop.documents?.length && ["documents", "Documents"], ["map", "Map"], ["contact", "Contact"]].filter(Boolean);

  return (
    <div className="bg-[#FAFAF9] text-stone-900 min-h-screen" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="public-listing">
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-white/70 border-b border-stone-200/60">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="text-lg tracking-tight truncate max-w-[45%]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{prop.address_line}</div>
          <nav className="hidden md:flex items-center gap-7 text-sm text-stone-600">
            {links.map(([id, label]) => <button key={id} onClick={() => scrollTo(id)} className="hover:text-stone-900 transition-colors">{label}</button>)}
          </nav>
          <button onClick={() => scrollTo("contact")} data-testid="request-info-top" className="bg-stone-900 text-white text-sm px-4 py-2 rounded-full hover:bg-stone-700 transition-colors">Request Info</button>
        </div>
      </header>

      <section className="relative h-[86vh] min-h-[560px] w-full overflow-hidden">
        {hero ? <img src={hero} alt={prop.address_line} className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 bg-stone-800" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/30" />
        {prop.listing_stage === "under_contract" && (
          <div data-testid="under-contract-ribbon" className="absolute top-0 right-0 h-36 w-36 overflow-hidden pointer-events-none z-50">
            <div className="absolute top-[30px] right-[-52px] w-[200px] rotate-45 bg-[#E8792B] text-white text-center text-xs font-bold uppercase tracking-wider py-2 shadow-lg">
              Under Contract
            </div>
          </div>
        )}
        {prop.listing_stage === "sold" && (
          <div data-testid="sold-ribbon" className="absolute top-0 right-0 h-36 w-36 overflow-hidden pointer-events-none z-50">
            <div className="absolute top-[30px] right-[-52px] w-[200px] rotate-45 bg-[#1F8A4C] text-white text-center text-xs font-bold uppercase tracking-wider py-2 shadow-lg">
              Sold
            </div>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0"><div className="max-w-6xl mx-auto px-6 pb-14 animate-fade-up">
          <div className="inline-flex items-center gap-2 text-white/90 text-xs uppercase tracking-[0.2em] mb-4"><span className="w-8 h-px bg-white/60" /> {prop.property_type} · {STAGE[prop.listing_stage] || "For Sale"}</div>
          <h1 className="text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{prop.address_line}</h1>
          <p className="text-white/80 mt-3 text-lg flex items-center gap-2"><MapPin className="w-4 h-4" /> {cityLine}</p>
          <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-3 text-white">
            <div className="text-3xl" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="hero-price">{fmtPrice(prop.price)}</div>
            <div className="flex items-center gap-6 text-sm text-white/90">
              {prop.beds != null && <span className="flex items-center gap-1.5"><BedDouble className="w-4 h-4" /> {prop.beds} Beds</span>}
              {prop.baths != null && <span className="flex items-center gap-1.5"><Bath className="w-4 h-4" /> {prop.baths} Baths</span>}
              {prop.sqft != null && <span className="flex items-center gap-1.5"><Ruler className="w-4 h-4" /> {prop.sqft?.toLocaleString()} Sq Ft</span>}
            </div>
          </div>
        </div></div>
      </section>

      <section id="details" className="max-w-6xl mx-auto px-6 py-24">
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-3">About this home</div>
            <p className="text-stone-700 text-lg leading-relaxed whitespace-pre-line font-light">{prop.description || "No description provided."}</p>
            {prop.features?.length > 0 && (
              <div className="mt-10">
                <h3 className="text-2xl mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Features & Highlights</h3>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                  {prop.features.map((f, i) => <div key={i} className="flex items-start gap-3 text-stone-700"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-stone-800 shrink-0" /> {f}</div>)}
                </div>
              </div>
            )}
          </div>
          <div className="lg:sticky lg:top-24 self-start">
            <div className="bg-white border border-stone-200 rounded-xl p-6">
              <div className="text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{fmtPrice(prop.price)}</div>
              <div className="mt-4 divide-y divide-stone-100 text-sm">
                <Fact icon={BedDouble} label="Bedrooms" value={prop.beds} /><Fact icon={Bath} label="Bathrooms" value={prop.baths} />
                <Fact icon={Ruler} label="Square Feet" value={prop.sqft?.toLocaleString()} /><Fact icon={Calendar} label="Year Built" value={prop.year_built} />
                <Fact icon={HomeIcon} label="Type" value={prop.property_type} />
              </div>
              <button onClick={() => scrollTo("contact")} className="mt-5 w-full bg-stone-900 text-white rounded-full py-3 text-sm font-medium hover:bg-stone-700 transition-colors">Request Information</button>
            </div>
          </div>
        </div>
      </section>

      {photos.length > 0 && (
        <section id="photos" className="max-w-6xl mx-auto px-6 pb-24">
          <div className="flex items-end justify-between mb-6"><h2 className="text-3xl tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Gallery</h2><span className="text-sm text-stone-400">{photos.length} Photos</span></div>
          <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-[200px] gap-3">
            {photos.map((p, i) => {
              const pat = i % 6;
              const span = pat === 0 ? "md:col-span-8 md:row-span-2" : pat === 1 ? "md:col-span-4" : pat === 2 ? "md:col-span-4" : pat === 3 ? "md:col-span-5" : pat === 4 ? "md:col-span-7" : "md:col-span-12";
              return <button key={i} onClick={() => setLightbox(i)} data-testid={`gallery-photo-${i}`} className={`relative overflow-hidden rounded-lg group ${span}`}><img src={p} alt={`Photo ${i + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" /></button>;
            })}
          </div>
        </section>
      )}

      {walk && (
        <section id="walkthrough" className="max-w-6xl mx-auto px-6 pb-24" data-testid="public-walkthrough">
          <h2 className="text-3xl tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Property Tour</h2>
          <div className="rounded-xl overflow-hidden border border-stone-200 bg-black aspect-video">
            <video data-testid="public-walkthrough-video" src={walk} poster={hero || undefined} controls playsInline preload="metadata" className="w-full h-full object-contain bg-black" onPlay={() => trackEvent("VideoPlay", { slug })} />
          </div>
        </section>
      )}

      {prop.video_url && (
        <section id="video" className="max-w-6xl mx-auto px-6 pb-24">
          <h2 className="text-3xl tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Video Tour</h2>
          <div className="relative rounded-xl overflow-hidden border border-stone-200 bg-black aspect-video" onClick={() => trackEvent("VideoPlay", { slug })}><iframe title="video" src={toEmbed(prop.video_url)} className="absolute inset-0 w-full h-full" allowFullScreen /></div>
        </section>
      )}
      {prop.tour_3d_url && (
        <section id="tour3d" className="max-w-6xl mx-auto px-6 pb-24">
          <h2 className="text-3xl tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>3D Walkthrough</h2>
          <div className="relative rounded-xl overflow-hidden border border-stone-200 bg-black aspect-video"><iframe title="3d" src={toEmbed(prop.tour_3d_url)} className="absolute inset-0 w-full h-full" allowFullScreen /></div>
        </section>
      )}

      {prop.floor_plans?.length > 0 && (
        <section id="floorplans" className="max-w-6xl mx-auto px-6 pb-24" data-testid="public-floorplans">
          <h2 className="text-3xl tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Floor Plans</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {prop.floor_plans.map((p, i) => {
              const isPdf = /\.pdf($|\?)/i.test(p.url) || (p.name || "").toLowerCase().endsWith(".pdf");
              return isPdf ? (
                <a key={i} href={p.url} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white p-5 hover:border-stone-400 transition-colors">
                  <FileText className="w-6 h-6 text-stone-700 shrink-0" /><span className="text-sm text-stone-700 truncate">{p.name || `Floor Plan ${i + 1}`}</span>
                </a>
              ) : (
                <a key={i} href={p.url} target="_blank" rel="noreferrer" className="block rounded-xl overflow-hidden border border-stone-200 bg-white hover:border-stone-400 transition-colors">
                  <img src={p.url} alt={`Floor plan ${i + 1}`} className="w-full h-56 object-contain bg-stone-50" />
                </a>
              );
            })}
          </div>
        </section>
      )}

      {prop.documents?.length > 0 && (
        <section id="documents" className="max-w-6xl mx-auto px-6 pb-24" data-testid="public-documents">
          <h2 className="text-3xl tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Documents & Disclosures</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {prop.documents.map((d, i) => (
              <a key={i} href={d.url} target="_blank" rel="noreferrer" data-testid={`public-doc-${i}`} className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white p-4 hover:border-stone-400 transition-colors">
                <FileText className="w-5 h-5 text-stone-700 shrink-0" />
                <span className="text-sm text-stone-700 truncate flex-1">{d.name || `Document ${i + 1}`}</span>
                <Download className="w-4 h-4 text-stone-400 shrink-0" />
              </a>
            ))}
          </div>
        </section>
      )}

      <section id="map" className="max-w-6xl mx-auto px-6 pb-24">
        <h2 className="text-3xl tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Location</h2>
        <div className="rounded-xl overflow-hidden border border-stone-200 h-[440px]"><iframe title="map" src={mapSrc} className="w-full h-full" loading="lazy" /></div>
        <p className="mt-3 text-stone-500 flex items-center gap-2"><MapPin className="w-4 h-4" /> {prop.address_line}, {cityLine}</p>
      </section>

      <section id="contact" className="bg-stone-900 text-white py-24">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-6">Presented By</div>
            {agent ? (
              <div className="flex items-start gap-5">
                {agent.headshot ? <img src={agent.headshot} alt={agent.name} className="w-28 h-28 rounded-lg object-cover" /> : <div className="w-28 h-28 rounded-lg bg-white/10" />}
                <div>
                  <div className="text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{agent.name}</div>
                  <div className="text-white/60 text-sm">{agent.title}</div><div className="text-white/60 text-sm">{agent.company}</div>
                  <div className="mt-4 space-y-2 text-sm">
                    {agent.phone && <a href={`tel:${agent.phone}`} className="flex items-center gap-2.5 text-white/90 hover:text-white"><Phone className="w-4 h-4" /> {agent.phone}</a>}
                    {agent.email && <a href={`mailto:${agent.email}`} className="flex items-center gap-2.5 text-white/90 hover:text-white"><Mail className="w-4 h-4" /> {agent.email}</a>}
                    {agent.web && <a href={agent.web.startsWith("http") ? agent.web : `https://${agent.web}`} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 text-white/90 hover:text-white"><Globe className="w-4 h-4" /> {agent.web}</a>}
                  </div>
                  {prop.agent_page && <a href={`/a/${prop.agent_page.username}`} data-testid="listing-agent-page-link" className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-[#E8A65A] hover:text-white transition-colors">View {agent.name || prop.agent_page.name}'s page →</a>}
                </div>
              </div>
            ) : <p className="text-white/50">Contact us for details on this property.</p>}
          </div>
          <LeadForm propertyId={prop.id} />
        </div>
      </section>

      <footer className="bg-stone-950 text-white/50 text-sm py-8 text-center">
        <div className="text-white text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>MattC Realty</div>
        <p className="mt-1">{prop.address_line}, {cityLine}</p>
      </footer>

      {lightbox !== null && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={() => setLightbox(null)} data-testid="lightbox">
          <button className="absolute top-5 right-5 text-white/80 hover:text-white" onClick={() => setLightbox(null)}><X className="w-7 h-7" /></button>
          <button className="absolute left-5 text-white/80 hover:text-white" onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + photos.length) % photos.length); }}><ChevronLeft className="w-9 h-9" /></button>
          <img src={photos[lightbox]} alt="" className="max-h-[85vh] max-w-[85vw] object-contain" onClick={(e) => e.stopPropagation()} />
          <button className="absolute right-5 text-white/80 hover:text-white" onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % photos.length); }}><ChevronRight className="w-9 h-9" /></button>
          <div className="absolute bottom-6 text-white/60 text-sm">{lightbox + 1} / {photos.length}</div>
        </div>
      )}
    </div>
  );
}

function Fact({ icon: Icon, label, value }) {
  if (value == null || value === "") return null;
  return <div className="flex items-center justify-between py-2.5"><span className="flex items-center gap-2 text-stone-500"><Icon className="w-4 h-4" /> {label}</span><span className="font-medium text-stone-900">{value}</span></div>;
}

function LeadForm({ propertyId }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", comment: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.phone) { toast.error("Please fill in your name, email and phone."); return; }
    setSending(true);
    try {
      await api.post("/lm/public/leads", { property_id: propertyId, ...form });
      trackLead();
      setSent(true); toast.success("Thanks! Your inquiry has been sent.");
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setSending(false); }
  };
  if (sent) return <div className="bg-white/5 border border-white/10 rounded-xl p-8 flex flex-col justify-center" data-testid="lead-success"><div className="text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Thank you.</div><p className="text-white/60 mt-2">Your inquiry has been received. An agent will reach out shortly.</p></div>;
  const cls = "w-full bg-white/5 border border-white/15 rounded-md px-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-white/40";
  return (
    <form onSubmit={submit} className="bg-white/5 border border-white/10 rounded-xl p-6" data-testid="lead-form">
      <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-5">Get in Touch</div>
      <div className="grid grid-cols-2 gap-3">
        <input data-testid="lead-first-name" placeholder="First Name *" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className={cls} />
        <input data-testid="lead-last-name" placeholder="Last Name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className={cls} />
        <input data-testid="lead-email" placeholder="Email *" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={`${cls} col-span-2`} />
        <input data-testid="lead-phone" placeholder="Phone *" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={`${cls} col-span-2`} />
        <textarea data-testid="lead-comment" placeholder="Comment" rows={4} value={form.comment} onChange={(e) => set("comment", e.target.value)} className={`${cls} col-span-2 resize-none`} />
      </div>
      <button type="submit" disabled={sending} data-testid="lead-submit" className="mt-4 w-full bg-white text-stone-900 rounded-full py-3 text-sm font-semibold hover:bg-white/90 transition-colors disabled:opacity-60">{sending ? "Sending…" : "Send Inquiry"}</button>
    </form>
  );
}
